import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAppByIdAsync } from '../../redux/reducer/apps/appSlice';
import { logout } from '../../redux/reducer/auth/authSlice';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  Divider,
  useTheme,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Category as CategoryIcon,
  Book as BookIcon,
  Topic as TopicIcon,
  CalendarMonth as CalendarIcon,
  Note as NoteIcon,
  Quiz as QuizIcon,
  Flag as FlagIcon,
  Assessment as AssessmentIcon,
  Info as InfoIcon,
  VpnKey as VpnKeyIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Notifications as NotificationsIcon,
  ExitToApp as LogoutIcon,
  Dashboard as DashboardIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';

const drawerWidth = 280;

const ModernSidebar = () => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [contentSubmenuOpen, setContentSubmenuOpen] = useState(true);
  const [adminSubmenuOpen, setAdminSubmenuOpen] = useState(true);

  const { appId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const app = useSelector((state) => state.apps.selectedApp);
  const userRole = localStorage.getItem('userRole');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (appId) {
      dispatch(fetchAppByIdAsync(appId));
    }
  }, [dispatch, appId]);

  // Close drawer on route change for consistent overlay behavior
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const handleToggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  const isActive = (path) => location.pathname === path;

  const handleNavigate = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  const contentMenuItems = [
    { text: 'Categories', icon: <CategoryIcon />, path: `/api/admin/app/${appId}/categories` },
    { text: 'Courses', icon: <BookIcon />, path: `/api/admin/app/${appId}/courses` },
    { text: 'Topics', icon: <TopicIcon />, path: `/api/admin/app/${appId}/topics` },
    { text: 'Years', icon: <CalendarIcon />, path: `/api/admin/app/${appId}/years` },
    { text: 'Notes', icon: <NoteIcon />, path: `/api/admin/app/${appId}/notes` },
    { text: 'Questions', icon: <QuizIcon />, path: `/api/admin/app/${appId}/questions` },
  ];

  const adminMenuItems = userRole === 'admin' ? [
    { text: 'Assessment', icon: <AssessmentIcon />, path: `/api/admin/app/${appId}/assessments` },
    { text: 'Flagged Questions', icon: <FlagIcon />, path: `/api/admin/app/${appId}/flagged-questions` },
    { text: 'Informations', icon: <InfoIcon />, path: `/api/admin/app/${appId}/informations` },
    { text: 'Activation Codes', icon: <VpnKeyIcon />, path: `/api/admin/app/${appId}/activation-codes` },
    { text: 'Users', icon: <PeopleIcon />, path: `/api/admin/app/${appId}/users` },
    { text: 'Notifications', icon: <NotificationsIcon />, path: `/api/admin/app/${appId}/notifications` },
    { text: 'Schools', icon: <SchoolIcon />, path: '/admin/schools' },
  ] : [{ text: 'Flagged Questions', icon: <FlagIcon />, path: `/api/admin/app/${appId}/flagged-questions` }, { text: 'Schools', icon: <SchoolIcon />, path: '/admin/schools' },];

  if (!app && appId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sidebar Header */}
      <Box
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
            }}
          >
            {user.name?.[0] || 'A'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight="600" noWrap>
              {user.name || 'Admin'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {userRole === 'admin' ? 'Administrator' : 'Instructor'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 2 }}>
        <List sx={{ px: 1 }}>
          {/* Back to Apps */}
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={() => handleNavigate('/admin/apps')}
              sx={{
                borderRadius: 2,
                mb: 1,
                backgroundColor: isActive('/admin/apps') ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.05)',
                },
              }}
            >
              <ListItemIcon sx={{ color: isActive('/admin/apps') ? 'primary.main' : 'text.secondary', minWidth: 40 }}>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText
                primary="Back to Apps"
                primaryTypographyProps={{
                  fontWeight: isActive('/admin/apps') ? 600 : 400,
                  color: isActive('/admin/apps') ? 'primary.main' : 'text.primary',
                }}
              />
            </ListItemButton>
          </ListItem>

          {/* App Details */}
          {app && (
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => handleNavigate(`/api/admin/app/${app._id}`)}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  backgroundColor: isActive(`/api/admin/app/${app._id}`) ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(99, 102, 241, 0.05)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: isActive(`/api/admin/app/${app._id}`) ? 'primary.main' : 'text.secondary', minWidth: 40 }}>
                  <DashboardIcon />
                </ListItemIcon>
                <ListItemText
                  primary="App Details"
                  primaryTypographyProps={{
                    fontWeight: isActive(`/api/admin/app/${app._id}`) ? 600 : 400,
                    color: isActive(`/api/admin/app/${app._id}`) ? 'primary.main' : 'text.primary',
                  }}
                />
              </ListItemButton>
            </ListItem>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Content Management Section */}
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={() => setContentSubmenuOpen(!contentSubmenuOpen)}
              sx={{ borderRadius: 2 }}
            >
              <ListItemText
                primary="Content Management"
                primaryTypographyProps={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                }}
              />
              {contentSubmenuOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </ListItemButton>
          </ListItem>

          <Collapse in={contentSubmenuOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {contentMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleNavigate(item.path)}
                    sx={{
                      borderRadius: 2,
                      pl: 3,
                      backgroundColor: isActive(item.path) ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(99, 102, 241, 0.05)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: isActive(item.path) ? 'primary.main' : 'text.secondary', minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: isActive(item.path) ? 600 : 400,
                        color: isActive(item.path) ? 'primary.main' : 'text.primary',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>

          {/* Admin Section */}
          {(userRole === 'admin' || userRole === 'instructor') && (
            <>
              <Divider sx={{ my: 2 }} />
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => setAdminSubmenuOpen(!adminSubmenuOpen)}
                  sx={{ borderRadius: 2 }}
                >
                  <ListItemText
                    primary="Administration"
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: 'text.secondary',
                    }}
                  />
                  {adminSubmenuOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </ListItemButton>
              </ListItem>

              <Collapse in={adminSubmenuOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {adminMenuItems.map((item) => (
                    <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigate(item.path)}
                        sx={{
                          borderRadius: 2,
                          pl: 3,
                          backgroundColor: isActive(item.path) ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(99, 102, 241, 0.05)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ color: isActive(item.path) ? 'primary.main' : 'text.secondary', minWidth: 40 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontWeight: isActive(item.path) ? 600 : 400,
                            color: isActive(item.path) ? 'primary.main' : 'text.primary',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          )}



        </List>
      </Box>

      {/* Logout Button */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            color: 'error.main',
            '&:hover': {
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'error.main', minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleToggleSidebar}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" fontWeight="600" sx={{ flexGrow: 1 }}>
            {app?.name || 'iStudy Admin'}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant="temporary"
        open={isOpen}
        onClose={handleToggleSidebar}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
        ModalProps={{ keepMounted: true }}
      >
        <Toolbar />
        {drawer}
      </Drawer>
    </>
  );
};

export default ModernSidebar;
